/**
 * Map of common currency symbols to their ISO 4217 code.
 * Covers the symbols most likely to appear as fallback values in the codebase.
 */
const SYMBOL_TO_ISO: Record<string, string> = {
  "€": "EUR",
  "$": "USD",
  "£": "GBP",
  "¥": "JPY",
  "₹": "INR",
  "₩": "KRW",
  "₺": "TRY",
  "₽": "RUB",
  "Fr": "CHF",
  "kr": "SEK",
  "zł": "PLN",
  "R$": "BRL",
  "RM": "MYR",
  "R": "ZAR",
  "฿": "THB",
  "₫": "VND",
  "₱": "PHP",
  "Kč": "CZK",
  "Ft": "HUF",
  "lei": "RON",
  "лв": "BGN",
  "kn": "HRK",
};

/**
 * Normalise a currency value that might be an ISO 4217 code **or** a bare
 * symbol (e.g. "€") into a valid ISO 4217 code that `Intl.NumberFormat`
 * accepts.
 */
function resolveIsoCurrency(raw: string): string {
  const trimmed = raw.trim();
  // Already an ISO code (3 ASCII letters)?
  if (/^[A-Za-z]{3}$/.test(trimmed)) return trimmed.toUpperCase();
  // Try symbol lookup
  return SYMBOL_TO_ISO[trimmed] ?? "EUR";
}

/**
 * Format a monetary amount using the correct symbol, decimal places, and
 * grouping for any ISO 4217 currency code (the same codes Stripe uses:
 * EUR, USD, GBP, JPY, …).
 *
 * The `currency` parameter also accepts bare symbols ("€", "$", …) which
 * are resolved to the corresponding ISO code automatically.
 *
 * @param amount   Numeric value (e.g. 5.5)
 * @param currency ISO 4217 code or symbol (e.g. "EUR", "€", "USD", "$")
 * @param locale   BCP 47 locale tag – defaults to "en" for consistent output
 * @returns        Formatted string, e.g. "€5.50", "$1,234.50", "¥500"
 */
export function formatPrice(
  amount: number,
  currency: string,
  locale: string = "en",
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: resolveIsoCurrency(currency || "EUR"),
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
